import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTonConnectUI } from '@tonconnect/ui-react';
import { toNano } from '@ton/core';
import { CONTRACT_ADDRESS, IS_CONTRACT_CONFIGURED, TICKET_PRICE_TON } from '../ton/config';
import { buildBuyTicketBody } from '../ton/messages';
import { sanitizeReferrerKey } from '../ton/referral';
import { resolveOutgoingReferrerKey } from '../ton/notify';
import { walletErrorTranslationKey } from '../ton/walletError';
import { gaMoneyEvent } from '../analytics';

interface BuyTicketState {
  pending: boolean;
  error: string | null;
  lastTxAt: number | null;
}

/**
 * Sends a ticket-purchase transaction via the connected wallet: a plain TON
 * transfer of `amountTon` (defaults to the ticket price) to the Pyramida
 * contract, optionally carrying a referrer key as a text comment.
 */
export function useBuyTicket() {
  const { t } = useTranslation();
  const [tonConnectUI] = useTonConnectUI();
  const [state, setState] = useState<BuyTicketState>({
    pending: false,
    error: null,
    lastTxAt: null,
  });

  const buyTicket = useCallback(
    async (referrerKey?: number | null, amountTon: number = TICKET_PRICE_TON) => {
      if (!IS_CONTRACT_CONFIGURED) {
        setState((s) => ({ ...s, error: t('lottery.notConfigured') }));
        return;
      }

      setState({ pending: true, error: null, lastTxAt: null });
      try {
        // Resolve the referrer for this purchase, in priority order:
        //   1. an explicit key passed by the caller,
        //   2. the key the app was opened with (`?ref=` / legacy start_param),
        //   3. the key the bot recorded for this Telegram user from a
        //      `t.me/<bot>?start=<key>` deep link,
        //   4. the key recorded for the connected wallet's address (covers
        //      plain browser tabs with no Telegram context).
        // The contract only honours it on a wallet's very first buy; for an
        // already-registered wallet it is ignored, so re-supplying is safe.
        const effectiveReferrer = sanitizeReferrerKey(
          referrerKey ??
            (await resolveOutgoingReferrerKey(tonConnectUI.account?.address ?? null)),
        );
        const body = buildBuyTicketBody(effectiveReferrer);
        await tonConnectUI.sendTransaction({
          validUntil: Math.floor(Date.now() / 1000) + 300,
          messages: [
            {
              address: CONTRACT_ADDRESS,
              amount: toNano(amountTon.toString()).toString(),
              ...(body ? { payload: body.toBoc().toString('base64') } : {}),
            },
          ],
        });
        gaMoneyEvent('buy_ticket', 'GRAM', amountTon);
        setState({ pending: false, error: null, lastTxAt: Date.now() });
      } catch (e) {
        setState({
          pending: false,
          error: t(walletErrorTranslationKey(e)),
          lastTxAt: null,
        });
      }
    },
    [t, tonConnectUI],
  );

  return { ...state, buyTicket };
}
